import React from "react";
import { motion } from "framer-motion";
import blogImage1 from "../../assets/blogs/colorado_14ers/b1.jpg";
import blogImage2 from "../../assets/blogs/colorado_14ers/b2.jpg";
import blogImage3 from "../../assets/blogs/colorado_14ers/b3.jpg";
import RecentPosts from "../../components/blogs/RecentPosts";
import Footer from "../../components/homepage/Footer"

export default function Colorado14ersBlog() {
  return ( <>
    <div className="flex max-w-[1200px] mx-auto gap-10 py-16 px-4">

      {/* LEFT BLOG CONTENT (80% WIDTH) WITH BLACK PANEL */}
      <motion.div
        className="w-full lg:w-3/4 bg-black/30 p-10 rounded-xl flex flex-col gap-12"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >

        {/* MAIN TOP IMAGE */}
        <div className="relative w-full h-[420px] md:h-[520px] rounded-xl overflow-hidden shadow-lg">
          <img
            src={blogImage1}
            alt="Colorado 14ers"
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-0 w-full bg-black/30 p-6 text-white">
            <h1 className="text-4xl md:text-5xl font-bold">
              How to Train for Colorado 14ers
            </h1>
            <p className="text-gray-200 text-lg">Kalon | November 26, 2025</p>
          </div>
        </div>

        {/* PARAGRAPH BLOCKS */}
        <div className="flex flex-col gap-8 text-gray-200 text-lg leading-relaxed">

          <p>
            Climbing Colorado’s 14ers is a major goal for many hikers. These summits rise above 14,000 feet and demand clear preparation. Proper training supports safety and endurance during long ascents at high altitude.
          </p>

          <p>
            The challenges include steep terrain, rapid weather changes, and reduced oxygen levels. Training should build strength, stamina, and stability.
          </p>

          {/* SECTION 1 WITH IMAGE ON RIGHT */}
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="flex-1">
<h2 className="text-2xl font-semibold text-neonTurquoise mb-3">
  1. Physical Conditioning
</h2>

<p className="mb-3">
  Physical conditioning supports the long and steady effort required for Colorado 14ers. Cardio sessions prepare the body for sustained movement at high elevation where oxygen levels drop. Leg strength improves stability during steep ascents and uneven ground. Core training supports balance, posture, and control while carrying a pack. Flexibility work helps prevent muscle fatigue and reduces the risk of strain during long hours on rocky terrain. A focused routine ensures the body handles each section of the climb with steady form and controlled effort.
</p>

<ul className="list-disc list-inside ml-4">
  <li>Cardio work such as running, stairs, and cycling.</li>
  <li>Leg training including squats, step-ups, and loaded hikes.</li>
  <li>Core training for stability during long climbs.</li>
  <li>Flexibility work to reduce strain on joints.</li>
</ul>

            </div>

            <img
              src={blogImage2}
              alt="Training hike"
              className="w-full md:w-[45%] h-[360px] object-cover rounded-xl shadow-lg"
            />
          </div>

          {/* SECTION 2 WITH IMAGE ON LEFT */}
          <div className="flex flex-col md:flex-row-reverse gap-6 items-start">
            <div className="flex-1">
<h2 className="text-2xl font-semibold text-neonTurquoise mb-3">
  2. Altitude Acclimatization
</h2>
<p>
  A gradual exposure to higher elevation helps the body adjust to reduced oxygen levels. This process lowers the chance of acute altitude issues and keeps physical output steady during ascent. The body responds through measured ventilation changes and improved circulation, so each stage of ascent must allow enough time for this adjustment.
</p>
<ul className="list-disc list-inside ml-4 mt-2">
  <li>Arrive early at a moderate elevation to allow your body to initiate basic adjustment.</li>
  <li>Do short hikes before the main climb to support progressive adaptation.</li>
  <li>Stay hydrated and maintain steady nutrition to support oxygen transport and energy balance.</li>
</ul>

            </div>

            <img
              src={blogImage3}
              alt="Summit view"
              className="w-full md:w-[45%] h-[360px] object-cover rounded-xl shadow-lg"
            />
          </div>

          {/* SECTION 3 – NUTRITION */}
          <div>
            <h2 className="text-2xl font-semibold text-neonTurquoise mb-3">
              3. Nutrition & Hydration
            </h2>

            <p>Nutrition supports energy and recovery during elevation exposure.</p>

            <table className="w-full text-left border border-gray-700 rounded-xl overflow-hidden mt-4">
              <thead className="bg-neonTurquoise/20 text-white">
                <tr>
                  <th className="px-4 py-2">Meal</th>
                  <th className="px-4 py-2">Recommendation</th>
                </tr>
              </thead>
              <tbody className="bg-gray-900 text-gray-200">
                <tr className="hover:bg-gray-800 transition">
                  <td className="px-4 py-2">Breakfast</td>
                  <td className="px-4 py-2">Protein-rich oats, eggs, and fruit.</td>
                </tr>
                <tr className="hover:bg-gray-800 transition">
                  <td className="px-4 py-2">Snack</td>
                  <td className="px-4 py-2">Trail mix, protein bars, and bananas.</td>
                </tr>
                <tr className="hover:bg-gray-800 transition">
                  <td className="px-4 py-2">Meals</td>
                  <td className="px-4 py-2">Balanced carbs, lean protein, and vegetables.</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* SECTION 4 – GEAR */}
          <div>
            <h2 className="text-2xl font-semibold text-neonTurquoise mb-3">
              4. Gear Essentials
            </h2>
            <ul className="list-disc list-inside ml-4">
              <li>Boots with ankle support.</li>
              <li>Layered clothing for weather changes.</li>
              <li>Poles for balance.</li>
              <li>Navigation and first-aid essentials.</li>
              <li>Hydration pack.</li>
            </ul>
          </div>

          {/* SECTION 5 – TRAINING PLAN */}
          <div>
            <h2 className="text-2xl font-semibold text-neonTurquoise mb-3">
              5. Training Plan Example (8 Weeks)
            </h2>
            <ol className="list-decimal list-inside ml-4">
              <li>Weeks 1–2: Build cardio base.</li>
              <li>Weeks 3–4: Strength and core sessions.</li>
              <li>Weeks 5–6: Longer endurance hikes.</li>
              <li>Weeks 7–8: Simulated peak hikes.</li>
            </ol>
          </div>

          <p>
            A structured approach improves endurance, stability, and altitude tolerance. With steady preparation, each Colorado 14er offers a rewarding and safe challenge.
          </p>

        </div>
      </motion.div>

      {/* RIGHT SIDEBAR — UNCHANGED */}
      <div className="hidden lg:block lg:w-1/4 sticky top-20 self-start">
        <RecentPosts />
      </div>
    </div>
        {/* FOOTER SHOULD BE INSIDE RETURN */}
    <Footer />
    </>
  );
}
